/**
 * ToleranceReviewPanel — "Checks still on the standard strictness: N of M"
 *
 * Every angle check carries a number of degrees that decides how far from the
 * coach's demo still counts as right, and almost none of them were chosen: the
 * studio shipped a preset equal to the model default, so 29 of the 41 live
 * checks sit exactly on it (study metric M8, ledger HC-26). This panel is
 * where the coach finds out, and the way into the editor to answer.
 *
 * COACH-ONLY. `metadata.tolerance_review` is in the backend's
 * `_WRITER_ONLY_METADATA_KEYS`, so it never reaches an athlete's or a public
 * viewer's payload and this panel renders nothing for them.
 *
 * `N` and `M` come from the backend summary (`on_default`, and the row count
 * via `on_default + coach_set`). The client never derives them by comparing a
 * check's degrees against a default of its own: that default is backend
 * configuration, and a stale copy here would misreport the coach's template
 * back to them.
 *
 * Copy comes from `@/utils/toleranceReviewCopy`; this component is layout only.
 */
import { AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';
import type { ToleranceReview } from '@/services/templateApi';
import {
  TOLERANCE_REVIEW_LINK_TEXT,
  toleranceReviewHeadline,
  toleranceReviewPanelCopy,
  toleranceReviewRowText,
} from '@/utils/toleranceReviewCopy';

export interface ToleranceReviewPanelProps {
  /** `metadata.tolerance_review` — absent on templates finalized before Phase 4. */
  review?: ToleranceReview | null;
  /** Opens the template studio, where the degrees are actually changed. */
  onReview?: () => void;
  className?: string;
}

export function ToleranceReviewPanel({
  review,
  onReview,
  className,
}: ToleranceReviewPanelProps) {
  // Nothing on the standard setting = nothing to review. Rendering an
  // "all clear" instead would put permanent chrome on every finalized
  // template for a question the coach has already answered.
  if (!review || review.on_default <= 0) return null;

  const total = review.on_default + review.coach_set;
  const onDefaultGoals = review.goals.filter(
    (goal) => goal.tolerance_source === 'default',
  );
  const copy = toleranceReviewPanelCopy();

  return (
    <section
      aria-label="Checks still using the standard strictness"
      data-testid="tolerance-review-panel"
      className={cn(
        'rounded-lg border border-border-subtle bg-dark-elevated-1/70 px-4 py-3',
        className,
      )}
    >
      <div className="flex items-start gap-2.5">
        <AdjustmentsHorizontalIcon
          className="mt-0.5 h-4 w-4 flex-shrink-0 text-text-muted"
          aria-hidden="true"
        />
        <div className="min-w-0 space-y-2">
          <h3
            className="text-sm font-semibold text-text-primary"
            data-testid="tolerance-review-headline"
          >
            {toleranceReviewHeadline(review.on_default, total)}
          </h3>
          <p className="text-sm leading-relaxed text-text-secondary">
            {copy.what} {copy.why}
          </p>
          <ul className="space-y-1">
            {onDefaultGoals.map((goal, index) => (
              <li
                key={goal.template_annotation_id || `${goal.label}-${index}`}
                data-testid="tolerance-review-row"
                className="text-sm leading-relaxed text-text-secondary"
              >
                {toleranceReviewRowText(goal.label, goal.tolerance)}
              </li>
            ))}
          </ul>
          <p className="text-sm leading-relaxed text-text-secondary">{copy.how}</p>
          {onReview && (
            <button
              type="button"
              onClick={onReview}
              className={cn(
                'rounded-lg border border-border-subtle px-3 py-1.5 text-sm font-medium',
                'text-text-primary transition-colors hover:bg-dark-elevated-2',
              )}
              data-testid="tolerance-review-link"
            >
              {TOLERANCE_REVIEW_LINK_TEXT}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

export default ToleranceReviewPanel;
